/**
 * PublicCTAPair — uniform "Learn More" + "Contact Us" button pair used
 * across every public marketing page.
 *
 * The user requested a single canonical CTA at every call site so that
 * the funnel ("learn about DDARP" or "talk to us") is consistent
 * regardless of which page the visitor is on. This component is the
 * lock-in.
 *
 * Variants
 * --------
 *   variant="light" — for use on light hero backgrounds (default)
 *   variant="dark"  — for use on dark hero backgrounds (Learn More
 *                     button gets a white border + translucent hover)
 *
 * The "Learn More" target defaults to /about-ddarp. Override via
 * the `learnMoreHref` prop when a page wants to cross-link elsewhere
 * (e.g., /about-us pointing back at /about-ddarp).
 *
 * Public Pages Refinements Phase 6 (2026-05-26).
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { Button } from './Button';
import { cn } from '@/lib/utils';


interface PublicCTAPairProps {
  /** Where "Learn More" navigates. Default /about-ddarp. */
  learnMoreHref?: string;
  /** Visual mode. Use 'dark' on dark hero backgrounds (gives Learn
   *  More button a white-translucent border so it reads on dark bg). */
  variant?: 'light' | 'dark';
  /** Optional extra className applied to the outer flex container. */
  className?: string;
}


export function PublicCTAPair({
  learnMoreHref = '/about-ddarp',
  variant = 'light',
  className,
}: PublicCTAPairProps) {
  const learnMoreClasses = variant === 'dark'
    ? 'border-white/30 text-white hover:bg-white/10'
    : '';

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-center gap-4',
        className,
      )}
    >
      <Link to={learnMoreHref}>
        <Button
          variant="outline"
          size="xl"
          className={learnMoreClasses}
        >
          Learn More
        </Button>
      </Link>
      <Link to="/contact">
        <Button
          size="xl"
          rightIcon={<ArrowRight size={20} />}
          className="shadow-lg shadow-primary/25"
        >
          Contact Us
        </Button>
      </Link>
    </div>
  );
}

export default PublicCTAPair;
